import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Wallet, Search, Clock, AlertTriangle, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

const R = (v) => `R${parseFloat(v || 0).toFixed(2)}`;
const day = (d) => (d ? new Date(d).toLocaleDateString('en-ZA') : '—');
const r2 = (n) => Math.round(n * 100) / 100;

// Stable empty list: a fresh [] on every render would re-fire the effect below
// each time, and the page then never lets a navigation through.
const NO_INVOICES = [];

const METHODS = [
  ['cash', '💵 Cash'],
  ['card', '💳 Card'],
  ['eft', '🏦 EFT'],
];

export default function ReceivePayment() {
  const qc = useQueryClient();
  const [client, setClient] = useState(null);
  const [search, setSearch] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [paidAt, setPaidAt] = useState(new Date().toISOString().split('T')[0]);
  const [reference, setReference] = useState('');
  const [picked, setPicked] = useState({});     // invoice id -> included?
  const [busy, setBusy] = useState(false);

  // The people who owe right now — clients pay when they collect, a day or two
  // after booking, so this is the fast path and needs no typing.
  const { data: recent = [], isLoading: recentLoading } = useQuery({
    queryKey: ['recent-unpaid'],
    queryFn: () => api.get('/billing/recent-unpaid', { params: { limit: 20 } }).then((r) => r.data),
  });

  const { data: results = [] } = useQuery({
    queryKey: ['client-search', search],
    queryFn: () => api.get('/clients', { params: { search } }).then((r) => r.data),
    enabled: search.trim().length >= 2,
  });

  const { data: openInvoicesData, isLoading: invLoading } = useQuery({
    queryKey: ['open-invoices', client?.id],
    queryFn: () => api.get(`/billing/clients/${client.id}/open-invoices`).then((r) => r.data),
    enabled: !!client,
  });
  const openInvoices = openInvoicesData || NO_INVOICES;

  // Default to every open invoice ticked, oldest first — the money then fills
  // them in order. Untick to aim the payment at particular invoices instead.
  useEffect(() => {
    setPicked(Object.fromEntries(openInvoices.map((i) => [i.id, true])));
  }, [openInvoices]);

  const owing = r2(openInvoices.reduce((s, i) => s + i.balance, 0));
  const selected = openInvoices.filter((i) => picked[i.id]);
  const selectedTotal = r2(selected.reduce((s, i) => s + i.balance, 0));
  const amt = parseFloat(amount) || 0;

  // Spread the money across the ticked invoices, oldest first.
  let left = amt;
  const allocation = selected.map((i) => {
    const applied = r2(Math.min(left, i.balance));
    left = r2(left - applied);
    return { invoice: i, applied };
  }).filter((a) => a.applied > 0);
  const allocated = r2(allocation.reduce((s, a) => s + a.applied, 0));
  const leftover = r2(amt - allocated);

  const pickClient = (c) => {
    setClient(c); setSearch(''); setAmount(''); setReference('');
  };

  const save = async () => {
    if (!client) return toast.error('Choose who is paying');
    if (amt <= 0) return toast.error('Enter the amount received');
    if (!allocation.length) return toast.error('Tick at least one invoice for the money to go against');

    if (leftover > 0.005) {
      const ok = window.confirm(
        `${client.name} is paying ${R(amt)} but only ${R(allocated)} can be allocated — ` +
        `${R(leftover)} is left over.\n\n` +
        `OK  = record only ${R(allocated)} (the leftover is NOT taken)\n` +
        `Cancel = go back and change the amount or tick more invoices`
      );
      if (!ok) return;
    }

    setBusy(true);
    try {
      const { data } = await api.post('/billing/payments/allocate', {
        client_id: client.id, method, paid_at: paidAt, reference: reference || null,
        allocations: allocation.map((a) => ({ invoice_id: a.invoice.id, amount: a.applied })),
      });
      toast.success(`${R(data.total)} received from ${client.name}`);
      setAmount(''); setReference('');
      qc.invalidateQueries({ queryKey: ['open-invoices'] });
      qc.invalidateQueries({ queryKey: ['recent-unpaid'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['debtors'] });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Payment failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center gap-3">
        <Wallet size={22} className="text-orange-500" />
        <div>
          <h1 className="text-xl font-bold text-gray-800">Receive Payment</h1>
          <p className="text-xs text-gray-400">Take money from a client and settle their invoices</p>
        </div>
      </div>

      {!client ? (
        <>
          {/* The fast path: who owes right now, newest first */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 pt-4 pb-2 flex items-center gap-2">
              <Clock size={15} className="text-orange-500" />
              <h2 className="font-semibold text-gray-700 text-sm">Recently invoiced — tap to take payment</h2>
            </div>
            {recentLoading ? <div className="p-8 text-center text-gray-400">Loading…</div>
            : recent.length === 0 ? <div className="p-8 text-center text-gray-400">Nobody owes anything 🎉</div>
            : (
              <div className="divide-y max-h-96 overflow-y-auto">
                {recent.map((c) => (
                  <button key={c.id} onClick={() => pickClient(c)}
                    className="w-full flex items-center justify-between px-5 py-3 hover:bg-orange-50 text-left">
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{c.name}</p>
                      <p className="text-xs text-gray-400">
                        {c.invoices} invoice{c.invoices === 1 ? '' : 's'}
                        {' · '}
                        {c.days_since === 0 ? 'invoiced today'
                          : c.days_since === 1 ? 'invoiced yesterday'
                          : `newest ${c.days_since} days ago`}
                        {c.phone ? ` · ${c.phone}` : ''}
                      </p>
                    </div>
                    <span className="font-bold text-gray-800">{R(c.owing)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Anyone not in the list */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <label className="block text-xs font-medium text-gray-500 mb-1">Someone else? Search all clients</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} autoFocus
                placeholder="Name, business, phone, email or account code…"
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
            {search.trim().length >= 2 && (
              <div className="mt-2 border rounded-lg divide-y max-h-64 overflow-y-auto">
                {results.length === 0 && <p className="px-3 py-3 text-sm text-gray-400">No client matches “{search}”</p>}
                {results.slice(0, 30).map((c) => (
                  <button key={c.id} onClick={() => pickClient(c)}
                    className="w-full text-left px-3 py-2 hover:bg-orange-50">
                    <p className="text-sm text-gray-800">{c.name}</p>
                    <p className="text-xs text-gray-400">
                      {[c.business_name !== c.name ? c.business_name : null, c.account_code, c.phone]
                        .filter(Boolean).join(' · ')}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Who's paying */}
          <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between">
            <div>
              <Link to={`/clients/${client.id}`} className="font-semibold text-gray-800 hover:text-orange-600">
                {client.name}
              </Link>
              <p className="text-xs text-gray-400">
                {invLoading ? 'Loading…' : `${openInvoices.length} open invoice${openInvoices.length === 1 ? '' : 's'} · owes ${R(owing)}`}
              </p>
            </div>
            <button onClick={() => { setClient(null); setPicked({}); setAmount(''); }}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 border rounded-lg px-3 py-1.5">
              <X size={13} /> Change client
            </button>
          </div>

          {openInvoices.length === 0 && !invLoading ? (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">
              {client.name} has nothing outstanding.
            </div>
          ) : (
            <>
              {/* The money */}
              <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Amount received *</label>
                    <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus
                      placeholder="0.00"
                      className="w-full border rounded-lg px-3 py-2 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
                    <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Reference <span className="text-gray-400">— receipt or bank reference</span>
                    </label>
                    <input value={reference} onChange={(e) => setReference(e.target.value)}
                      placeholder="e.g. CAP 16.04"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                </div>
                <div className="flex gap-2">
                  {METHODS.map(([k, lbl]) => (
                    <button key={k} onClick={() => setMethod(k)}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium border ${
                        method === k ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:border-orange-400'
                      }`}>{lbl}</button>
                  ))}
                </div>
                <button onClick={() => setAmount(String(owing))}
                  className="text-xs text-orange-500 hover:underline">Paying everything ({R(owing)})</button>
              </div>

              {/* Where it goes */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="px-5 pt-4 pb-2 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-700 text-sm">What this settles</h3>
                  <p className="text-xs text-gray-400">Oldest first — untick to skip an invoice</p>
                </div>
                <div className="divide-y max-h-72 overflow-y-auto">
                  {openInvoices.map((i) => {
                    const a = allocation.find((x) => x.invoice.id === i.id);
                    return (
                      <label key={i.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 sm:px-5 py-2.5 hover:bg-gray-50 cursor-pointer">
                        <input type="checkbox" checked={!!picked[i.id]}
                          onChange={(e) => setPicked((p) => ({ ...p, [i.id]: e.target.checked }))}
                          className="accent-orange-500" />
                        <span className="font-mono text-xs font-semibold text-gray-700">
                          {i.invoice_number}
                          {i.source === 'quickbooks' && (
                            <span className="ml-1.5 font-sans text-[10px] bg-gray-100 text-gray-500 rounded px-1 py-0.5">QuickBooks</span>
                          )}
                        </span>
                        <span className="text-xs text-gray-400">{day(i.created_at)}</span>
                        <span className="ml-auto text-xs text-gray-500">owing {R(i.balance)}</span>
                        <span className={`text-xs font-semibold w-20 sm:w-24 text-right ${a ? 'text-green-600' : 'text-gray-300'}`}>
                          {a ? `− ${R(a.applied)}` : '—'}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div className="px-5 py-3 bg-gray-50 border-t space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Selected invoices</span>
                    <span className="text-gray-700">{R(selectedTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Being allocated</span>
                    <span className="font-semibold text-green-700">{R(allocated)}</span>
                  </div>
                  {leftover > 0.005 && (
                    <p className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                      <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>{R(leftover)} left over.</strong> That is more than the ticked invoices need.
                        Either lower the amount, tick more invoices, or continue and only {R(allocated)} will
                        be recorded — you&apos;ll be asked to confirm.
                      </span>
                    </p>
                  )}
                </div>
              </div>

              <button onClick={save} disabled={busy || amt <= 0}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl text-base font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                <Check size={18} />
                {busy ? 'Saving…' : `Receive ${R(allocated || amt)} from ${client.name}`}
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}
